import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type SaudeStatus = 'healthy' | 'warning' | 'critical';
export type StatusConexao = 'conectada' | 'instavel' | 'fora' | 'verificando';

export interface SaudeConexao {
  health_status: SaudeStatus;
  reason: string | null;
  connection_state: string | null;
}

/**
 * FONTE UNICA de status de conexao WhatsApp no front.
 *
 * Le whatsapp_connection_health (populada pelo whatsapp-health-watchdog a cada
 * 5min a partir da API Evolution + atividade real). NAO confia no connection_state
 * 'open' cru do Evolution, que MENTE em sessao fantasma (socket morto mas open).
 *
 * Todas as telas que mostram "Conectada/Caida" devem derivar daqui via statusDe(id),
 * pra nunca mais haver contradicao entre telas.
 */
export function statusConexaoDe(hs?: SaudeStatus | null, state?: string | null): StatusConexao {
  if (hs === 'critical') return 'fora';
  if (hs === 'warning') return 'instavel';
  // healthy com connection_state 'close' = zumbi: o watchdog pode levar ate 1 ciclo
  // pra rebaixar, mas o state do webhook ja diz FECHADA — bolinha nunca fica verde
  // (caso Renan 0692 08/07: verde fake enquanto as msgs morriam no vacuo). (08/07)
  if (hs === 'healthy') return (state ?? 'open') === 'open' ? 'conectada' : 'instavel';
  return 'verificando';
}

export function useSaudeConexoes() {
  const { data } = useQuery({
    queryKey: ['saude-conexoes-global'],
    refetchInterval: 20_000,
    staleTime: 10_000,
    queryFn: async () => {
      const { data } = await supabase
        .from('whatsapp_connection_health')
        .select('connection_id, health_status, reason, connection_state');
      const map = new Map<string, SaudeConexao>();
      (data || []).forEach((r: any) =>
        map.set(r.connection_id, {
          health_status: r.health_status,
          reason: r.reason,
          connection_state: r.connection_state,
        }),
      );
      return map;
    },
  });

  const byId = data ?? new Map<string, SaudeConexao>();
  return {
    byId,
    pronto: !!data,
    saudeDe: (id?: string | null): SaudeConexao | undefined => (id ? byId.get(id) : undefined),
    statusDe: (id?: string | null): StatusConexao => {
      const h = id ? byId.get(id) : undefined;
      return statusConexaoDe(h?.health_status, h?.connection_state);
    },
  };
}
